<template>
  <PopupModal
    v-if="!cookieExists || forceOpen"
    :open="cookieConsentModalOpen"
    class="md:min-w-[610px] z-50"
  >
    <template #header
      ><h3 class="textHeadline3 mb-8">
        {{ $t("cookie.consent.title") }}
      </h3></template
    >
    <template #body>
      <div class="px-3">
        <div class="text-left border-b-2 pb-3">
          {{ $t("cookie.consent.text") }}
        </div>
        <div class="border-b-2 pb-3">
          <div class="flex flex-col mt-3">
            <div class="flex w-full">
              <div class="w-2/3 text-left">
                <h3 class="font-bold break-words">
                  {{ $t("cookie.consent.analytics.cookies.title") }}
                </h3>
                <div class="break-words">
                  {{ $t("cookie.consent.analytics.cookies.text") }}
                </div>
              </div>

              <div class="flex w-1/3 justify-center items-center">
                <Switch
                  v-model="analyticCookies"
                  :class="[
                    analyticCookies
                      ? 'bg-bkk-100 '
                      : 'bg-white border border-mid-gray',
                    'relative z-50 inline-flex h-[32px] w-[52px] shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out',
                  ]"
                  @click="analyticCookies = !analyticCookies"
                >
                  <span
                    aria-hidden="true"
                    :class="[
                      analyticCookies
                        ? 'translate-x-[22px] bg-white size-[24px] translate-y-[4px]'
                        : 'translate-x-[6px] bg-mid-gray size-[17px] translate-y-[7px]',
                      'inline-block transform rounded-full shadow ring-0 transition duration-200',
                    ]"
                  />
                </Switch>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="flex flex-row justify-center gap-2 flex-wrap">
        <ButtonElement
          :disabled-visible="true"
          :label="$t('cookie.consent.save.settings.button')"
          @click="handleSaveSettings"
        />
        <ButtonElement
          :label="$t('cookie.consent.accept.all.button')"
          :class="handleIsSbk() ? ' !text-background' : ''"
          @click="handleAcceptAll"
        />
      </div>
      <div class="mt-3 text-center">
        <a
          class="underline"
          target="_blank"
          href="https://www.mein-phileo.de/datenschutz"
          >{{ $t("cookie.consent.data.privacy.button") }}</a
        >
        |
        <a
          class="underline"
          target="_blank"
          href="https://www.mein-phileo.de/impressum"
          >{{ $t("cookie.consent.imprint.button") }}</a
        >
      </div>
    </template>
  </PopupModal>
</template>

<script>
import PopupModal from "./PopupModal.vue";
import { Switch } from "@headlessui/vue";
import VueMatomo from "vue-matomo";
import { getMatomoConfig } from "~/matomoConfig";
import { useClientStore } from "../../../store/v2/client";
import ButtonElement from "../form/ButtonElement.vue";
import {
  useCookieConsent,
  COOKIE_NAME,
  COOKIE_ANALYTICS,
  COOKIE_NONE,
} from "../../../plugins/useCookieConsent";

export default {
  components: { PopupModal, Switch, ButtonElement },
  props: {
    forceOpen: Boolean,
  },
  emits: ["onSaveSettings", "onSettingsLoaded"],
  setup() {
    const instance = getCurrentInstance();
    const clientStore = useClientStore();
    const cookieConsent = useCookieConsent(instance, clientStore);
    return { instance, clientStore, cookieConsent };
  },
  setup() {
    const instance = getCurrentInstance();
    const clientStore = useClientStore();
    const {
      readConsentCookie,
      acceptAll,
      saveSettings,
      deleteAllCookies,
      createCookieConsentCookie,
      getSettingsPayload,
      onCookieSettingsLoaded,
      onSaveCookieSettings,
      installPlugins,
      isSbk,
    } = useCookieConsent(instance, clientStore);

    const cookieConsentModalOpen = ref(true);
    const cookieExists = ref(false);
    const cookieExistsOnLoad = ref(false);
    const analyticCookies = ref(false);

    onMounted(() => {
      const result = readConsentCookie();
      analyticCookies.value = result.analyticCookies;
      cookieExists.value = result.cookieExists;
      cookieExistsOnLoad.value = result.cookieExistsOnLoad;

      if (
        (!cookieExists.value && !instance.props.forceOpen) ||
        instance.props.forceOpen
      ) {
        cookieConsentModalOpen.value = true;
      }

      const settings = getSettingsPayload(
        cookieExists.value,
        analyticCookies.value,
        cookieExistsOnLoad.value
      );
    });

    function handleAcceptAll() {
      acceptAll(
        () => handleSaveSettings(),
        (val) => {
          analyticCookies.value = val;
        }
      );
    }

    function handleSaveSettings() {
      saveSettings(
        () => deleteAllCookies(),
        () => createCookieConsentCookie(analyticCookies.value),
        () =>
          getSettingsPayload(
            cookieExists.value,
            analyticCookies.value,
            cookieExistsOnLoad.value
          ),
        instance.emit,
        (settings) =>
          onSaveCookieSettings(
            settings,
            (settings) => installPlugins(settings),
            (open) => {
              cookieConsentModalOpen.value = open;
            }
          ),
        (open) => {
          cookieConsentModalOpen.value = open;
        },
        (exists) => {
          cookieExists.value = exists;
        }
      );
    }

    function handleIsSbk() {
      return isSbk();
    }

    return {
      cookieConsentModalOpen,
      cookieExists,
      cookieExistsOnLoad,
      analyticCookies,
      handleAcceptAll,
      handleSaveSettings,
      handleIsSbk,
    };
  },
};
</script>
